import React, { useRef, useState } from 'react';
import { map } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import { useTranslation } from 'react-i18next';
import {
  HotelDescription,
  HotelDetails,
  HotelHighlight,
  HotelHighlights,
  HotelInfo,
  HotelName,
  HotelRegion,
  HotelStarRating,
  HotelStarText,
  StyledHotel,
} from './Hotel.styles';
import { IHotel } from 'services/BackendApi';
import Rooms from '../../containers/Rooms';
import LinkButton from 'components/LinkButton';
import Star from 'ui/Icons/star.component.svg';
import { theme } from '../../../tailwind.config';
import { ImagesCarousel } from 'ui/ImagesCarousel';
import { ReviewSummary } from 'containers/ReviewPage/ReviewSummary';

const renderFeature = value => <HotelHighlight key={value}>{value}</HotelHighlight>;

export interface IHotelProps extends IHotel {
  id: string;
  photos: { uuid: string; displayName: string; url: string }[];
  lodgings?: any[];
  className?: string;
}
export const Hotel = ({
  id,
  amenities,
  description,
  name,
  photos,
  region,
  starRating,
  overview,
  highlights,
  countryCode,
  lodgings,
  className,
}: IHotelProps) => {
  const [showAmenities, setShowAmenities] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const [seeHighlights, setSeeHighlights] = useState(false);
  const { t } = useTranslation();

  return (
    <StyledHotel id="hotel" className={className}>
      <HotelDetails id="hotel-details">
        <ImagesCarousel
          images={photos}
          showArrows={true}
          showThumbnails={true}
          carouselImageClassName="h-[500px]"
          thumbnailsCarouselClassName="h-[70px]"
        />
        <HotelInfo id="hotel-info">
          <HotelStarRating>
            <Star className="mr-5px" fill={theme.colors['brown-100']} />{' '}
            <HotelStarText>
              <>
                {starRating} {t('star')}
              </>
            </HotelStarText>
          </HotelStarRating>
          <HotelName>{name}</HotelName>
          {region && <HotelRegion>{region}</HotelRegion>}
          <ReviewSummary hotelUuid={id} className='mt-[-10px]'/>
          <HotelDescription>{description}</HotelDescription>

          {!isNilOrEmpty(overview) && (
            <LinkButton
              title={showOverview ? t('labels.hideOverview') : t('labels.seeOverview')}
              aria-label={showOverview ? t('labels.hideOverview') : t('labels.seeOverview')}
              className="linkButton linkButton--in-list"
              onClick={() => setShowOverview(!showOverview)}
            >
              {showOverview ? `- ${t('labels.hideOverview')}` : `+ ${t('labels.seeOverview')}`}
            </LinkButton>
          )}
          {showOverview && <HotelHighlights>{map(renderFeature, overview)}</HotelHighlights>}

          {!isNilOrEmpty(highlights) && (
            <LinkButton
              title={seeHighlights ? t('labels.hideHighlights') : t('labels.seeHighlights')}
              aria-label={seeHighlights ? t('labels.hideHighlights') : t('labels.seeHighlights')}
              className="linkButton linkButton--in-list"
              onClick={() => setSeeHighlights(!seeHighlights)}
            >
              {seeHighlights ? `- ${t('labels.hideHighlights')}` : `+ ${t('labels.seeHighlights')}`}
            </LinkButton>
          )}
          {seeHighlights && <HotelHighlights>{map(renderFeature, highlights)}</HotelHighlights>}

          {!isNilOrEmpty(amenities) && (
            <LinkButton
              title={showAmenities ? t('labels.hideAmenities') : t('labels.seeAmenities')}
              className="linkButton linkButton--in-list"
              aria-label={showAmenities ? t('labels.hideAmenities') : t('labels.seeAmenities')}
              onClick={() => setShowAmenities(!showAmenities)}
            >
              {showAmenities ? `- ${t('labels.hideAmenities')}` : `+ ${t('labels.seeAmenities')}`}
            </LinkButton>
          )}
          {showAmenities && <HotelHighlights>{map(renderFeature, amenities)}</HotelHighlights>}
        </HotelInfo>
        {/* the "left hand menu" of accommodations */}
        <Rooms hotelUuid={id} lodgings={lodgings} />
      </HotelDetails>
    </StyledHotel>
  );
};

export default Hotel;
